'use client';

import { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Play,
  Clock,
  User,
  TrendingUp,
  Bookmark,
  Share2,
  MoreVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { UnifiedMomentResult } from '@/lib/services/unified-search-service';
import { SaveToCollectionButton } from '@/components/collections/SaveToCollectionButton';
import Link from 'next/link';
import { MomentModal } from './MomentModal';
import { MuxClipPlayer } from './MuxClipPlayer';

interface MomentsGridProps {
  moments: UnifiedMomentResult[];
  selectionMode?: boolean;
  selectedMoments?: Set<string>;
  onToggleSelection?: (momentId: string) => void;
  onToggleAll?: () => void;
  onTitleUpdate?: (momentId: string, newTitle: string) => Promise<void>;
  editable?: boolean;
  className?: string;
}

const MOMENT_TYPE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  chapter: { label: 'Chapter', color: 'bg-blue-500', icon: '📖' },
  highlight: { label: 'Highlight', color: 'bg-yellow-500', icon: '✨' },
  quote: { label: 'Quote', color: 'bg-apprendo-500', icon: '💬' },
  topic: { label: 'Topic', color: 'bg-green-500', icon: '🏷️' },
  action: { label: 'Action', color: 'bg-red-500', icon: '✅' },
  faq: { label: 'Q&A', color: 'bg-indigo-500', icon: '❓' },
  sentiment: { label: 'Sentiment', color: 'bg-pink-500', icon: '😊' },
  playbook: { label: 'Playbook', color: 'bg-orange-500', icon: '📋' },
  // B2B Marketing types
  testimonial: { label: 'Testimonial', color: 'bg-emerald-500', icon: '💚' },
  case_study: { label: 'Case Study', color: 'bg-cyan-500', icon: '🏆' },
  pain_point: { label: 'Pain Point', color: 'bg-rose-500', icon: '⚠️' },
  objection_handling: { label: 'Objection', color: 'bg-violet-500', icon: '🛡️' },
  social_proof: { label: 'Social Proof', color: 'bg-amber-500', icon: '📈' },
};

export function MomentsGrid({
  moments,
  selectionMode = false,
  selectedMoments = new Set(),
  onToggleSelection,
  onToggleAll,
  onTitleUpdate,
  editable = false,
  className,
}: MomentsGridProps) {
  const [hoveredMoment, setHoveredMoment] = useState<string | null>(null);
  const [selectedMoment, setSelectedMoment] = useState<UnifiedMomentResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingTitleId && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitleId]);

  const handleTitleDoubleClick = (e: React.MouseEvent, moment: UnifiedMomentResult) => {
    if (selectionMode || !editable || !onTitleUpdate) return;
    e.stopPropagation();
    setEditingTitleId(moment.id);
    setEditTitleValue(moment.title || moment.description || '');
  };

  const handleTitleSave = async (momentId: string) => {
    const trimmed = editTitleValue.trim();
    if (!trimmed || !onTitleUpdate) {
      setEditingTitleId(null);
      return;
    }
    const originalMoment = moments.find((m) => m.id === momentId);
    if (trimmed === (originalMoment?.title || originalMoment?.description)) {
      setEditingTitleId(null);
      return;
    }
    setIsSavingTitle(true);
    try {
      await onTitleUpdate(momentId, trimmed);
      setEditingTitleId(null);
    } catch {
      // keep editing on failure
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent, momentId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave(momentId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTitleId(null);
    }
  };


  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMomentClick = (moment: UnifiedMomentResult) => {
    if (selectionMode && onToggleSelection) {
      onToggleSelection(moment.id);
    } else {
      setSelectedMoment(moment);
      setModalOpen(true);
    }
  };

  if (moments.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No moments found</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Select All Checkbox (in selection mode) */}
      {selectionMode && onToggleAll && (
        <div className="flex items-center gap-2 pb-2 border-b">
          <Checkbox
            checked={selectedMoments.size === moments.length && moments.length > 0}
            onCheckedChange={onToggleAll}
          />
          <span className="text-sm text-muted-foreground">
            {selectedMoments.size > 0
              ? `${selectedMoments.size} selected`
              : 'Select all'}
          </span>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {moments.map((moment) => {
          const isSelected = selectedMoments.has(moment.id);
          const isHovered = hoveredMoment === moment.id;
          const typeConfig = moment.momentType
            ? MOMENT_TYPE_CONFIG[moment.momentType]
            : null;

          return (
            <Card
              key={moment.id}
              className={cn(
                'group relative transition-all duration-200',
                'hover:shadow-lg hover:border-primary/50',
                isSelected && 'ring-2 ring-primary border-primary'
              )}
              onMouseEnter={() => setHoveredMoment(moment.id)}
              onMouseLeave={() => setHoveredMoment(null)}
            >
              {/* Selection Checkbox (top-left corner) */}
              {selectionMode && onToggleSelection && (
                <div className="absolute top-3 left-3 z-10">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggleSelection(moment.id)}
                    className="bg-background/90 backdrop-blur"
                  />
                </div>
              )}

              <div
                onClick={() => handleMomentClick(moment)}
                className="cursor-pointer"
              >
                <CardContent className="p-0">
                  {/* Thumbnail / Video Preview */}
                  <div className="relative aspect-video bg-muted overflow-hidden">
                    {/* Show video clip preview on hover if we have muxPlaybackId */}
                    {isHovered && moment.muxPlaybackId && !selectionMode ? (
                      <div className="absolute inset-0 z-10">
                        <MuxClipPlayer
                          playbackId={moment.muxPlaybackId}
                          startTime={moment.startTime}
                          endTime={moment.endTime}
                          autoPlay
                          muted
                          loop
                          controls={false}
                          className="w-full h-full"
                        />
                      </div>
                    ) : moment.thumbnailUrl ? (
                      <img
                        src={moment.thumbnailUrl}
                        alt={moment.title || moment.description}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}

                    {/* Duration Overlay. For multi-segment compilations we
                        show "N segments · M:SS total" instead of the envelope
                        width — the rendered video is the SUM of segments,
                        not (end - start). */}
                    <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur px-2 py-1 rounded text-xs text-white font-medium">
                      {(() => {
                        const segs = moment.segments;
                        if (segs && segs.length > 1) {
                          const totalSec = segs.reduce(
                            (sum, s) => sum + Math.max(0, s.end_time - s.start_time),
                            0
                          );
                          return `${segs.length} segments · ${formatDuration(totalSec)}`;
                        }
                        return formatDuration(moment.endTime - moment.startTime);
                      })()}
                    </div>

                    {/* Moment Type Badge */}
                    {typeConfig && (
                      <div className="absolute top-2 right-2">
                        <Badge className={cn('text-white', typeConfig.color)}>
                          <span className="mr-1">{typeConfig.icon}</span>
                          {typeConfig.label}
                        </Badge>
                      </div>
                    )}

                    {/* Play Button Overlay (on hover, only if no video preview) */}
                    {isHovered && !selectionMode && !moment.muxPlaybackId && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                          <Play className="h-8 w-8 text-primary-foreground ml-1" fill="currentColor" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    {/* Title */}
                    {editingTitleId === moment.id ? (
                      <input
                        ref={titleInputRef}
                        value={editTitleValue}
                        onChange={(e) => setEditTitleValue(e.target.value)}
                        onKeyDown={(e) => handleTitleKeyDown(e, moment.id)}
                        onBlur={() => handleTitleSave(moment.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isSavingTitle}
                        maxLength={100}
                        className="font-semibold leading-snug w-full bg-transparent border-b-2 border-primary outline-none"
                      />
                    ) : (
                      <h3
                        className={cn(
                          "font-semibold line-clamp-2 leading-snug",
                          editable && onTitleUpdate && !selectionMode && "cursor-text"
                        )}
                        onDoubleClick={(e) => handleTitleDoubleClick(e, moment)}
                      >
                        {moment.title || moment.description}
                      </h3>
                    )}

                    {/* Description (if title exists) */}
                    {moment.title && moment.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {moment.description}
                      </p>
                    )}

                    {/* Metadata Row */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatTimestamp(moment.startTime)}</span>
                      </div>

                      {moment.engagementScore && (
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          <span>{(moment.engagementScore * 100).toFixed(0)}%</span>
                        </div>
                      )}

                      {moment.speaker && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{moment.speaker}</span>
                        </div>
                      )}

                      {/* Source Badge */}
                      <Badge variant="outline" className="ml-auto text-[10px] py-0 h-5">
                        {moment.source === 'post_moment' ? 'Curated' : 'Transcript'}
                      </Badge>
                    </div>

                    {/* Host Info */}
                    {moment.host && (
                      <div className="flex items-center gap-2 pt-2 border-t">
                        {moment.host.avatarUrl ? (
                          <img
                            src={moment.host.avatarUrl}
                            alt={moment.host.name}
                            className="w-6 h-6 rounded-full"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                            <User className="h-3 w-3" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{moment.host.name}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </div>

              {/* Action Buttons (bottom-right corner on hover) */}
              {!selectionMode && isHovered && (
                <div className="absolute bottom-4 right-4 flex gap-2">
                  <SaveToCollectionButton
                    postId={moment.postId}
                    startTime={moment.startTime}
                    endTime={moment.endTime}
                    title={moment.title}
                    description={moment.description}
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur shadow-md"
                  />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Moment Modal */}
      <MomentModal
        moment={selectedMoment}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
}
